import React, {
	memo,
	useCallback,
	useState,
	FunctionComponent,
	useEffect,
} from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'

import styles from './styles'
import { StoreValue, SetUserOnBoarding } from 'store'
import { UserOnboardingStateValue, UserProvider } from './reducer'
import {
	Step1,
	Step2,
	Step3,
	Step4,
	OnboardingProgress,
} from './components/index'

const useStyles = makeStyles(styles)
const STEP_1 = 0
const STEP_2 = 1
const STEP_3 = 2
const STEP_4 = 3
const STEP_5 = 4

const UserOnboarding: FunctionComponent<any> = props => {
	const {
		firstName,
		lastName,
		aboutMe,
		skills,
		values,
		experience,
		userCv,
	} = props
	const classes = useStyles()
	const { t, i18n } = useTranslation()
	const [activeStep, setActiveStep] = useState(0)
	const [skipped, setSkipped] = useState(new Set<number>())
	const [basicInfoStep, setBasicInfoStep] = useState(false)
	const [detailsStep, setDetailsStep] = useState(false)
	const [experienceStep, setExperienceStep] = useState(false)
	const [userCvStep, setUserCvStep] = useState(false)
	const [highlightProgressBar, setHighlightProgressBar] = useState(false)
	// const intervalId = useRef<any>()
	const [handleSetUserOnBoarding] = useMutation(SetUserOnBoarding)

	const isStepOptional = useCallback((step: number) => {
		return step === 1
	}, [])

	const isStepSkipped = useCallback(
		(step: number) => {
			return skipped.has(step)
		},
		[skipped],
	)

	const handleBack = useCallback(() => {
		setActiveStep(prevActiveStep => prevActiveStep - 1)
	}, [])

	const handleNext = useCallback(() => {
		let newSkipped = skipped
		if (isStepSkipped(activeStep)) {
			newSkipped = new Set(newSkipped.values())
			newSkipped.delete(activeStep)
		}

		setActiveStep(prevActiveStep => prevActiveStep + 1)
		setSkipped(newSkipped)
	}, [activeStep, isStepSkipped, skipped])

	const handleSkip = useCallback(() => {
		if (!isStepOptional(activeStep)) {
			// You probably want to guard against something like this,
			// it should never occur unless someone's actively trying to break something.
			throw new Error("You can't skip a step that isn't optional.")
		}

		setActiveStep(prevActiveStep => prevActiveStep + 1)
		setSkipped(prevSkipped => {
			const newSkipped = new Set(prevSkipped.values())
			newSkipped.add(activeStep)
			return newSkipped
		})
	}, [isStepOptional, activeStep, setActiveStep, setSkipped])

	const handleSelectedStep = useCallback(
		(number: number) => {
			setActiveStep(number)
		},
		[setActiveStep],
	)

	const renderCurrentStep = useCallback(() => {
		switch (activeStep) {
			case STEP_1:
				return <Step1 handleNext={handleNext} />
			case STEP_2:
				return (
					<Step2
						handleNext={handleNext}
						handleBack={handleBack}
						handleSkip={handleSkip}
					/>
				)
			case STEP_3:
				return (
					<Step3
						handleNext={handleNext}
						handleBack={handleBack}
						handleSkip={handleSkip}
					/>
				)
			case STEP_4:
				return <Step4 handleNext={handleNext} handleBack={handleBack} />
		}
	}, [activeStep, handleBack, handleNext, handleSkip])

	useEffect(() => {
		if (highlightProgressBar && activeStep !== STEP_5) {
			setHighlightProgressBar(false)
		}
	}, [highlightProgressBar, activeStep])

	useEffect(() => {
		let checkDetails =
			skills && skills.length > 0 && values && values.length > 0
		if (firstName && lastName && aboutMe) {
			setBasicInfoStep(true)
		} else {
			setBasicInfoStep(false)
		}

		if (checkDetails) {
			setDetailsStep(true)
		} else {
			setDetailsStep(false)
		}
		const {
			title,
			description,
			startDatetime,
			endDatetime,
			company,
			isPresent,
		} = experience

		if (
			title &&
			description &&
			startDatetime &&
			(endDatetime || isPresent) &&
			company
		) {
			setExperienceStep(true)
		} else {
			setExperienceStep(false)
		}
		if (userCv) {
			setUserCvStep(true)
		} else {
			setUserCvStep(false)
		}
	}, [
		firstName,
		lastName,
		aboutMe,
		skills,
		values,
		experience,
		userCv,
		userCvStep,
	])

	useEffect(() => {
		handleSetUserOnBoarding({ variables: { language: i18n.language } })
	}, [i18n, handleSetUserOnBoarding])

	return (
		<>
			<Grid container={true} justify="center" direction="row">
				<Grid className={classes.onBoardRoot}>
					{
						<Grid item className={classes.progressBarContainer}>
							{highlightProgressBar && (
								<div
									className={classes.progressBarContainerText}
								>
									{t(
										'Choose the step you want to change from the ones below.',
									)}
								</div>
							)}
							<OnboardingProgress
								basicInfoStep={basicInfoStep}
								detailsStep={detailsStep}
								experienceStep={experienceStep}
								userCvStep={userCvStep}
								handleSelectedStep={handleSelectedStep}
								activeStep={activeStep}
								highlightProgressBar={highlightProgressBar}
							/>
						</Grid>
					}
					<Grid
						container
						justify="center"
						className={classes.jobContainer}
					>
						<Grid item>{renderCurrentStep()}</Grid>
					</Grid>
				</Grid>
			</Grid>
		</>
	)
}

const UserWithOnboardingState = (props: any) => {
	return (
		<UserOnboardingStateValue
			keys={[
				'firstName',
				'lastName',
				'aboutMe',
				'imageProfile',
				'imageAvatar',
				'jobType',
				'skills',
				'values',
				'experience',
				'userCv',
			]}
		>
			<UserOnboarding {...props} />
		</UserOnboardingStateValue>
	)
}

const UserOnboardingCreation = (props: any) => (
	<StoreValue
		keys={[
			'user.id|storeUserId',
			'user._ig|storeUserIg',
			'user.uid|storeUserUid',
			'user.profile|storeUserProfile',
		]}
	>
		<UserWithOnboardingState {...props} />
	</StoreValue>
)

export default memo((props: any) => (
	<UserProvider>
		<UserOnboardingCreation {...props} />
	</UserProvider>
))
